import React, { useCallback, useEffect, useRef, useState } from "react";
import { ExplanatoryCard, Modal } from "@truevault/aquilo";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import {
  ConsentBannerSettings,
  ConsentBannerSettingsHandle,
  useConsentBannerSettings,
} from "../../components/superorg/settings/ConsentBannerSettings";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../hooks";
import { CMPSettingsDto } from "../../../common/service/server/dto/CMPSettingsDto";
import { Api } from "../../../common/service/Api";
import { RouteHelpers, Routes } from "../../root/routes";
import { Callout, CalloutVariant } from "../../components/Callout";
import { Link } from "react-router-dom";
import { TopNavigationBreadcrumb } from "../../components/privacyCenter/TopNavigationBreadcrumb";
import { TabPanel } from "../../components";
import clsx from "clsx";
import { CookieScannerTab } from "../stay-compliant/CookieScannerTab";
import { useCookieScanner } from "../../components/organisms/cookies/CookieScanner";
import { useHistory, useLocation } from "react-router";
import { TrueScanTab } from "../stay-compliant/TrueScanTab";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { useOrganizationFeatureCookieConsent } from "../../hooks/useOrganizationFeatures";
import { ConsentRecordListingWrapper } from "../../components/organisms/consent/ConsentRecordListing";
import { ConsumerConsentLookup } from "../../components/organisms/consent/ConsumerConsentLookup";
import TabsWithUnderline from "@truevault/aquilo/src/components/tabs/TabsWithUnderline";
import { useOnboardingProgress } from "../../../common/hooks/useOnboardingProgress";
import { ProgressFooter, ProgressFooterActions } from "../get-compliant/ProgressFooter";

export const PrivacyCenterConsentBanner = () => {
  const routerHistory = useHistory();
  const [organization, orgRequest] = usePrimaryOrganization();
  const organizationId = usePrimaryOrganizationId();
  const [showModal, setShowModal] = useState(true);

  const showCookieLookup = useOrganizationFeatureCookieConsent(organization);

  // Ref for calling the consent banner form's submit method
  const consentBannerSettingsRef = useRef<ConsentBannerSettingsHandle>();

  // Check if the org is still in onboarding (hasn't completed website consent setup)
  const [onboardingProgress] = useOnboardingProgress(organizationId);
  const isWebsiteConsentOnboardingComplete =
    onboardingProgress?.privacyCenterOnboardingProgress?.isWebsiteConsentSetup ?? false;

  let tabOptions = [
    { label: "Consent Banner", value: "consentbannersettings" },
    { label: "Cookie Scan", value: "cookiescanner" },
    {
      label: (
        <>
          <span>
            TrueScan
            <span
              className="text-xs text-neutral-500 ml-1"
              style={{
                backgroundColor: "#9581da",
                padding: "4px",
                borderRadius: "4px",
                color: "white",
                marginLeft: "8px",
                fontSize: "14px",
                position: "relative",
                top: "-2px",
              }}
            >
              Beta
            </span>
          </span>
        </>
      ),
      value: "truescan",
    },
  ];

  if (showCookieLookup) {
    tabOptions.push({
      label: "Website Consent Records",
      value: "consentrecords",
    });
    tabOptions.push({
      label: "Consumer Consent Lookup",
      value: "consumerconsentlookup",
    });
  }

  const location = useLocation();
  const [tab, setTab] = useState<string>(
    location.pathname === Routes.cookies.root
      ? "cookiescanner"
      : location.pathname === Routes.records.websiteConsentRecords
      ? "consentrecords"
      : location.pathname === Routes.records.consumerConsentLookup
      ? "consumerconsentlookup"
      : location.pathname === Routes.trueScan.root
      ? "truescan"
      : "consentbannersettings",
  );

  const tabNameByValue: Record<string, string> = {
    consentbannersettings: "Consent Banner",
    cookiescanner: "Cookie Scan",
    truescan: "TrueScan",
    consentrecords: "Website Consent Records",
    consumerconsentlookup: "Consumer Consent Lookup",
  };

  useEffect(() => {
    const route = tab;
    if (route === "cookiescanner") {
      history.replaceState(null, null, Routes.cookies.root);
    } else if (route === "consentbannersettings") {
      history.replaceState(null, null, Routes.privacyCenter.consentBanner);
    } else if (route === "consentrecords") {
      history.replaceState(null, null, Routes.records.websiteConsentRecords);
    } else if (route === "consumerconsentlookup") {
      history.replaceState(null, null, Routes.records.consumerConsentLookup);
    } else if (route === "truescan") {
      history.replaceState(null, null, Routes.trueScan.root);
    }
  }, [tab]);

  //
  // Consent banner settings hooks
  //
  const { settings, privacyCenter, privacyCenterRequest, cmpCustomizationRequest, getLatestData } =
    useConsentBannerSettings();

  const [saveSuccess, setSaveSuccess] = useState(false);

  const neverPublished = !(
    settings.canadaConsentManagementVariant ||
    settings.usConsentManagementVariantNonUSP ||
    settings.usConsentManagementVariantCA ||
    settings.usConsentManagementVariantUSP ||
    settings.usConsentManagementVariantMinors ||
    settings.eeaukConsentManagementVariant
  );

  const updatePrivacyCenterCMPCustomization = useCallback(
    (submitValues: CMPSettingsDto) =>
      Api.privacyCenter
        .postPrivacyCenterCMPCustomization(organization?.id, privacyCenter?.id, submitValues)
        .then(() => {
          getLatestData();
          setSaveSuccess(true);
          setTimeout(
            () => {
              setSaveSuccess(false);
            },
            privacyCenter?.cmpVersion ? 5000 : 15000,
          ); /* Additional time to read the warning message compared to success message */
        }),
    [organization?.id, privacyCenter?.id, privacyCenter?.cmpVersion, getLatestData],
  );

  let isWebsiteConsentSetup = checkCmpCompletionStatus(settings);

  // Keep track of whether to show the footer in a separate state variable that is only initialized on page load, so we know to persist the footer even after the user has completed this step
  const [showFooter, setShowFooter] = useState<boolean | null>(null);
  useEffect(() => {
    if (showFooter === null && onboardingProgress) {
      // Show footer if org hasn't completed the website consent onboarding step
      setShowFooter(!isWebsiteConsentOnboardingComplete);
    }
  }, [onboardingProgress, showFooter, setShowFooter, isWebsiteConsentOnboardingComplete]);

  //
  // Cookie scanner hooks
  //
  const scanner = useCookieScanner();

  return (
    <StandardPageWrapper
      requests={[
        orgRequest,
        privacyCenterRequest,
        cmpCustomizationRequest,
        scanner.requests.cookieScanRequest,
        scanner.requests.cookieRecommendationsRequest,
        scanner.requests.organizationDataRecipientsRequest,
        scanner.requests.vendorCatalogRequest,
      ]}
      footer={
        showFooter && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with Banner Configuration"
                nextOnClick={async () => {
                  // Publish the consent banner settings before advancing
                  if (consentBannerSettingsRef.current) {
                    const success = await consentBannerSettingsRef.current.submitForm();
                    // Only navigate if publish was successful
                    if (success) {
                      routerHistory.push(Routes.privacyCenter.dashboard + "?confetti=1");
                    }
                  }
                }}
              />
            }
          />
        )
      }
    >
      <TopNavigationBreadcrumb title="Website Consent" />
      <div className="tw-boundary">
        <TabsWithUnderline
          tabs={tabOptions.map((t) => ({
            name: tabNameByValue[t.value] || String(t.label),
            current: tab === t.value,
            onClick: () => setTab(t.value),
            labelNode:
              t.value === "truescan" ? (
                <span>
                  TrueScan
                  <span
                    className="text-xs text-neutral-500 ml-1"
                    style={{
                      backgroundColor: "#9581da",
                      padding: "4px",
                      borderRadius: "4px",
                      color: "white",
                      marginLeft: "8px",
                      fontSize: "14px",
                      position: "relative",
                      top: "-2px",
                    }}
                  >
                    Beta
                  </span>
                </span>
              ) : undefined,
          }))}
        />
      </div>
      <TabPanel value="consentbannersettings" tab={tab}>
        <div className="max-width-896">
          <div className={clsx("privacy-center", "mt-md")}>
            <div className="tw-boundary">
              <ExplanatoryCard
                topBody={
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                      What is a Consent Banner?
                    </h3>
                    <p className="text-sm font-normal leading-5 text-gray-500">
                      It's a pop-up that appears on your website and informs visitors of your data
                      collection practices. Typically, it asks visitors for consent (and allows them
                      to remove consent) for online tracking related to analytics, advertising, and
                      site personalization.
                    </p>
                  </div>
                }
                hiddenBody={
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3 pt-3">
                        How does it work?
                      </h3>
                      <div className="text-sm font-normal leading-5 text-gray-500 space-y-4">
                        <p>
                          <strong>Opt-In</strong> → No non-essential cookies fire unless a consumer
                          "accepts" (or toggles on) tracking for a non-essential purpose, like
                          advertising or analytics.
                        </p>
                        <p>
                          <strong>Opt-Out</strong> → When a consumer toggles a consent type (like
                          advertising) to "off," your website will stop collecting and transmitting
                          data to third parties (like ad networks) for the specified purpose of
                          advertising.
                        </p>
                        <p>
                          For consent to function properly, you must add the TrueVault Javascript to
                          your site by following the Developer Instructions in the previous step,
                          including the Google Tag Manager configuration.
                        </p>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3 pt-3">
                        Is a banner required?
                      </h3>
                      <p className="text-sm font-normal leading-5 text-gray-500">
                        Under US privacy laws, a consent banner is not required. However, many
                        businesses use a banner in order to offer greater choices to visitors. If
                        you do use a banner, legally you are required to ensure it works. Our
                        TrueScan feature checks that your banner functions properly after you set it
                        up.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3 pt-3">
                        How to preview your banner before going live:
                      </h3>
                      <p className="text-sm font-normal leading-5 text-gray-500">
                        If you'd like to check out your banner before it goes live on your site,
                        simply follow the technical setup instructions in Developer Instructions,
                        configure your banner by making selections below, and then click the
                        "Developer Preview" option at the bottom of this page.
                      </p>
                    </div>
                  </div>
                }
              />
            </div>

            {neverPublished && (
              <Callout
                variant={CalloutVariant.Secondary}
                title="In order to customize your consent banner, your website must be using the latest
              version of the Polaris JavaScript. To do so, complete the following steps:"
                className="mb-md"
                icon={<InformationCircleIcon className="w-6 h-6 mr-2" />}
              >
                <ol>
                  <li>
                    Input your Google Analytics Measurement ID in your{" "}
                    <Link to={Routes.privacyCenter.integrations}>
                      <strong>Integrations</strong>
                    </Link>
                  </li>
                  <li>
                    Update the Polaris JavaScript on your site found in Step 2 of your{" "}
                    <a
                      href={RouteHelpers.instructions.id(organization?.devInstructionsId)}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <strong>Developer Instructions</strong>
                    </a>
                  </li>
                  <li>
                    Run the{" "}
                    <a href={Routes.websiteAudit.root}>
                      <strong>Website Audit</strong>
                    </a>{" "}
                    to ensure your JavaScript was updated correctly
                  </li>
                  <li>
                    Follow our{" "}
                    <a
                      href="https://knowledge.truevault.com/article/213-setting-up-cookie-consent-in-google-tag-manager"
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <strong>GTM setup</strong>
                    </a>{" "}
                    article to ensure third-party scripts on your website are categorized with
                    consent requirements and the consent-changed event is configured
                  </li>
                  <li>Enable the appropriate Banner below</li>
                </ol>
              </Callout>
            )}
            {(!isWebsiteConsentSetup || neverPublished) && showModal && (
              <Modal
                title="Configure your website consent"
                body="On this page you can enable a consent banner on your website and customize it to fit your brand. This is optional."
                firstButton="Continue"
                firstButtonColor="primary"
                widthButton="full"
                showOverlay={true}
                closeOnOverlayClick={true}
                onFirstButtonClick={() => setShowModal(false)}
              >
                <div className="tw-boundary">
                  <div className="flex justify-center items-center font-roboto font-medium text-sm leading-5 text-center text-neutral-600">
                    Learn more about Consent Banners in{" "}
                    <InformationCircleIcon className="w-6 h-6 mr-2 ml-2" /> Resources and Tips
                  </div>
                </div>
                <img
                  src="/assets/images/stay_compliant/privacy_center/consent-banner-modal-image.png"
                  alt="tutorial modal intro"
                  className="mx-auto"
                  style={{
                    width: "635px",
                    height: "500px",
                    objectFit: "contain" as const,
                    display: "inline",
                  }}
                />
              </Modal>
            )}

            <ConsentBannerSettings
              ref={consentBannerSettingsRef}
              isConfirmedCustomizableCMPVersion={(privacyCenter?.cmpVersion ?? "") !== ""}
              onUpdateCMPCustomization={updatePrivacyCenterCMPCustomization}
              saveSuccess={saveSuccess}
              organization={organization}
              privacyCenterURL={privacyCenter?.url}
              privacyCenterPublicId={privacyCenter?.id}
              {...settings}
            />
          </div>
        </div>
      </TabPanel>
      <TabPanel value="cookiescanner" tab={tab}>
        <CookieScannerTab {...scanner.props} />
      </TabPanel>
      <TabPanel value="truescan" tab={tab}>
        <TrueScanTab />
      </TabPanel>
      <TabPanel value="consentrecords" tab={tab}>
        <ConsentRecordListingWrapper />
      </TabPanel>
      <TabPanel value="consumerconsentlookup" tab={tab}>
        <ConsumerConsentLookup />
      </TabPanel>
    </StandardPageWrapper>
  );
};

function checkCmpCompletionStatus(cmpSettings: CMPSettingsDto) {
  return !!(
    cmpSettings?.canadaConsentManagementVariant ||
    cmpSettings?.usConsentManagementVariantNonUSP ||
    cmpSettings?.usConsentManagementVariantCA ||
    cmpSettings?.usConsentManagementVariantMinors ||
    cmpSettings?.usConsentManagementVariantUSP ||
    (cmpSettings?.eeaukConsentManagementVariant &&
      cmpSettings.eeaukConsentManagementVariant !== "INTERACTIVE_DEFAULT_NO_CONSENT")
  );
}
